import * as React from "react";
import {createHashRouter, RouterProvider, Navigate, RouteObject} from "react-router-dom";
import './styles/app.scss';
import Theme from "./components/common/Theme";
import './services/nativeBridge';
import {disableZoom} from "./services/misc";
import "./services/playbackOrchestrator/midiNotesPlayer";
import EllipseSVGClipPath from "./components/common/EllipseSVGClipPath";

// Replace Sentry's error boundary with this one in time periods when we want to use LogRocket.
import ErrorBoundary from "./components/common/ErrorBoundary";

import "./services/appRating/appRating";
import {ProductMonitoringIdentify} from "./modules/logging/product-monitoring-identify/ProductMonitoringIdentify";
import {ReduxProvider} from "./redux/ReduxProvider";
import {appRoutePaths} from "./config/config";
import CoursesPage from "./components/courses/CoursesPage";
import AppRoute from "./AppRoute";
import DashboardPage from "./components/dashboard/DashboardPage";
import CourseLessonsPage from "./components/courseLessons/CourseLessonsPage";
import LessonPage from "./components/lesson/LessonPage";
import SettingsPage from "./components/settings/SettingsPage";
import BootCampPage from "./components/bootCamp/BootCampPage";
import UnlockedSongLessonsPage from "./pages/unlocked-song-lessons/UnlockedSongLessonsPage";
import {SongsRouterPage} from "./pages/songs-router/SongsRouterPage";
import SignInPage from "./components/signIn/SignInPage";
import SocialLinkEmailPage from "./components/signIn/socialLink/SocialLinkEmailPage";
import SocialLinkCodePage from "./components/signIn/socialLink/SocialLinkCodePage";
import InfoPage from "./components/onboarding/info/InfoPage";
import SetupYourKeyboard from "./components/onboarding/keyboardWizard/SetupYourKeyboard";
import SetupKeyboardLater from "./components/onboarding/keyboardWizard/SetupKeyboardLater";
import SetupFeedbackAndScoring from "./components/onboarding/setupFeedbackAndScoring/SetupFeedbackAndScoring";
import PitchDetectionSelectedPage from "./components/onboarding/setupFeedbackAndScoring/PitchDetectionSelectedPage";
import {ComponentPlayground} from "./pages/component-playground/ComponentPlayground";
import {Playlist} from "./components/playlist/Playlist";
import * as Sentry from "@sentry/react";
import RouterErrorMessage from "./components/common/RouterErrorMessage";
import ErrorMessage from "./components/common/errorMessage/ErrorMessage";
import {MIDIConnection} from "./components/common/MIDIConnection";
import {microphone} from "./hooks/microphone.hook";
import {microphoneStore} from "./redux/microphone/microphoneStore";
import {ComponentLibrary} from "./pages/component-library/ComponentLibrary";
import {ButtonDocumentation} from "./pages/component-library/button/ButtonDocumentation";
import {GenericModalDocumentation} from "./pages/component-library/genericModal/GenericModalDocumentation";
import {BasicPageDocumentation} from "./pages/component-library/basicPage/BasicPageDocumentation";
import KeyboardWizardPage from "./components/onboarding/keyboardWizard/KeyboardWizardPage";
import {NativeBridge} from "./components/common/NativeBridge";
import AllowMicrophoneAccess
  from "./components/onboarding/setupFeedbackAndScoring/allowMicrophoneAccess/AllowMicrophoneAccess";
import MicAccessNeeded from "./components/onboarding/setupFeedbackAndScoring/micAccessNeeded/MicAccessNeeded";
import YouAreAllSet from "./components/onboarding/setupFeedbackAndScoring/youAreAllSet/YouAreAllSet";
import CreateTrialAccountPage from "./components/createTrialAccount/CreateTrialAccountPage";
import DisclosureAndConsentPage from "components/onboarding/disclosureAndConsent/DisclosureAndConsentPage";
import {ContainerDocumentation} from "./pages/component-library/container/ContainerDocumentation";
import {InfoButtonDocumentation} from "./pages/component-library/infoButton/InfoButtonDocumentation";
import {ToggleDocumentation} from "./pages/component-library/toggle/ToggleDocumentation";
import {RadioGroupDocumentation} from "./pages/component-library/radioGroup/RadioGroupDocumentation";
import CreateAccountPage from "./components/onboarding/createAccount/CreateAccountPage";
import {TextBoxDocumentation} from "./pages/component-library/textBox/TextBoxDocumentation";

/**
 * In ReactRouter v5, each route entry looked something like one of these:
 *   <AppRoute path={appRoutePaths.lastPageUsed}/>
 *   <AppRoute path={appRoutePaths.coursesPage} element={<CoursesPage/>} />
 * Now, in ReactRouter v6, each route entry looks like this:
 *   { path: {appRoutePaths.lastPageUsed}, element: <AppRoute path={appRoutePaths.lastPageUsed} element={element}/>, errorElement: <ErrorMessage> }
 * This function is a helper to make the transition from v5 to v6 easier,
 * by reducing repetition and increasing clarity,
 * at the cost of increased indirection.
 */
const getRoute = (path: string, element?: React.ReactNode, loader?: RouteObject["loader"]): RouteObject => {
  return {
    path: path,
    element: <AppRoute path={path} element={element}/>,
    errorElement: <RouterErrorMessage/>,
    loader: loader,
  };
}

// While checking microphone permissions within this method does manipulate Redux,
// components which do not use the hook useAppSelector cannot guarantee that they are getting fresh data from Redux
// by calling a getting it directly, even though this loader runs before rendering.
// Those components should subscribe to Redux changes if they want to guarantee fresh data.
const loadMicrophonePermissions = async () => {
  const isChecked = microphoneStore.getPermissionStatus() != "notset";

  if (!isChecked) {
    await microphone.checkPermissions(false);
  }

  // Data which is returned here is available in userLoaderData().
  // However, since that's only usable in some areas of our code, and to keep a single approach to getting that data,
  // Return null
  // This causes all components to use the same approach to consuming this data.
  // Use Redux (e.g. microphoneStore.getPermissionStatus(), which is updated by microphone.checkPermissions())
  return null;
}

/** Export the router to list routes programmatically (see the sidebar in the component library) */
export const router= createHashRouter([
  getRoute(appRoutePaths.lastPageUsed),
  getRoute(appRoutePaths.coursesPage, <CoursesPage/>),
  getRoute(appRoutePaths.courseLessonsPage, <CourseLessonsPage/>),
  getRoute(appRoutePaths.lessonPage, <LessonPage />, loadMicrophonePermissions),
  getRoute(appRoutePaths.settingsPage, <SettingsPage/>),
  getRoute(appRoutePaths.bootCampPage, <BootCampPage/>),
  getRoute(appRoutePaths.unlockedSongLessonsPage, <UnlockedSongLessonsPage/>),
  getRoute(appRoutePaths.dashboardPage, <DashboardPage />),
  getRoute(appRoutePaths.playlistPage, <Playlist/>),
  getRoute(appRoutePaths.songsRouterPage, <SongsRouterPage/>),
  getRoute(appRoutePaths.signInPage, <SignInPage/>),
  getRoute(appRoutePaths.socialLinkEmail, <SocialLinkEmailPage/>),
  getRoute(appRoutePaths.socialLinkCode, <SocialLinkCodePage/>),
  getRoute(appRoutePaths.createAccountPage, <CreateAccountPage />, loadMicrophonePermissions),
  getRoute(appRoutePaths.createTrialAccountPage, <CreateTrialAccountPage />, loadMicrophonePermissions),
  getRoute(appRoutePaths.infoPage, <InfoPage/>),
  getRoute(appRoutePaths.keyboardWizard, <KeyboardWizardPage />),
  getRoute(appRoutePaths.setupYourKeyboardPage, <SetupYourKeyboard/>),
  getRoute(appRoutePaths.onboarding_disclosureAndConsent, <DisclosureAndConsentPage />),
  getRoute(appRoutePaths.onboarding_setupFeedback_allowMicrophoneAccess, <AllowMicrophoneAccess/>),
  getRoute(appRoutePaths.onboarding_setupFeedback_micAccessNeeded, <MicAccessNeeded/>),
  getRoute(appRoutePaths.onboarding_setupFeedback_youAreAllSet, <YouAreAllSet/>),
  getRoute(appRoutePaths.onboarding_setupFeedback_setupFeedbackAndScoring, <SetupFeedbackAndScoring/>),
  getRoute(appRoutePaths.setupKeyboardLaterPage, <SetupKeyboardLater/>),
  getRoute(appRoutePaths.pitchDetectionSelected, <PitchDetectionSelectedPage/>),
  getRoute(appRoutePaths.componentPlaygroundPage, <ComponentPlayground/>),

  /**
   * This is different from all the other routes for two reasons:
   * 1) It is a nested route, so it has child routes that use a common layout.
   * 2) It isn't used by end users, only developers.
   */
  {path: "component-library", element:<ComponentLibrary/>, children: [
    {path: "button", element: <ButtonDocumentation/>}, // /component-library/buttons
    {path: "generic-modal", element: <GenericModalDocumentation/>},   // /component-library/modals
    {path: "basic-page", element: <BasicPageDocumentation/>},   // /component-library/basic-page
    {path: "container", element: <ContainerDocumentation/>},   // /component-library/container
    {path: "info-button", element: <InfoButtonDocumentation/>},   // /component-library/info-button
    {path: "radio-group", element: <RadioGroupDocumentation/>, handle: {group: "Form Controls" as const}},   // /component-library/radio-group
    {path: "text-box", element: <TextBoxDocumentation/>, handle: {group: "Form Controls" as const}}, // /component-library/text-box
    {path: "toggle", element: <ToggleDocumentation/>, handle: {group: "Form Controls" as const}}   // /component-library/toggle
  ]},
  getRoute('*', <Navigate to={appRoutePaths.dashboardPage}/>) // not found route
]);

disableZoom();
// https://github.com/wmonk/create-react-app-typescript/issues/375#issuecomment-426330603
// https://github.com/wmonk/create-react-app-typescript/issues/375#issuecomment-426359022
function App() {
  return <ReduxProvider>
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        // In development, this tag won't be set because React will rethrow errors caught within an error boundary
        // to the global error handler, and Sentry only records that error, without the tag.
        // To test functionality in this method, build the app locally for production
        // with `npm run build` followed by `serve -s build` (after installing the "serve" package globally).
        // https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/#:~:text=In%20development%20mode,%20React%20will%20rethrow%20errors%20caught%20within%20an%20error%20boundary%20to%20the%20global%20error%20handler.
        scope.setTag("isFromErrorBoundary", "yes");
      }}
      fallback={({error, componentStack, resetError}) => (
        <div>
          <h1>Something Went Wrong</h1>
          <div>
            <a
              href={`mailto:support@playgroundsessions.com?subject=App Error Report&body=Hello, I encountered this error message in the app: \n ${(error as Error)?.stack?.toString()}`}>Email
              this error to support</a>
          </div>
          <br/>
          <button style={{appearance: "revert", border: "revert", cursor: "revert", backgroundColor: "revert"}}
            onClick={() => {resetError(); window.location.reload()}}>
            Click here to reload.
          </button>
          <br/>
          <div>{(error as Error)?.toString()}</div>
          <div>{componentStack}</div>
        </div>
      )}>
      <ProductMonitoringIdentify />
      <Theme>
        <EllipseSVGClipPath/>
        <MIDIConnection/>
        <NativeBridge/>
        <RouterProvider router={router} />
        <div className={"invisible-title-bar"} aria-hidden={true}/>
        <ErrorMessage/>
      </Theme>
    </Sentry.ErrorBoundary>
  </ReduxProvider>
}

export default App;
